/**
 * RegistrationForm — the Registration step's three field sections
 * (General Information · Sample Information · Report Requirements), laid out
 * exactly as the approved prototype and the legacy XAF view
 * `Samplecheckin_DetailView_Copy_SampleRegistration`.
 *
 * Field order and behaviours follow the user manual's Entry Guidance table
 * (surfaced here as a hover tooltip on every label):
 *   · LEFT column  = Job ID → … → Date Expect
 *   · RIGHT column = Relinquished By → … → Status
 *   · Client and Sample Matrix are the only REQUIRED fields.
 *   · Sample Matrix drives the flow: Test and No Of Samples stay GATED until a
 *     matrix is chosen ("without a sample matrix, how can you select a test?").
 *   · Client / Project selections auto-fill their read-only dependants.
 *   · TAT sets Due Date (Received Date + TAT).
 *
 * Presentational only — all state lives in SampleRegistrationDetailPage.
 */
import type { ReactNode } from 'react';
import { LookupCombo } from './LookupCombo';
import {
  GUIDANCE, NEWABLE, type SrHeader, type SrLookup, type SrLookupKey,
} from './srData';

export interface RegistrationFormProps {
  header: SrHeader;
  /** Patch one or more header fields. */
  patch: (p: Partial<SrHeader>) => void;
  /** The live lookup catalogs (quick-adds prepend to these). */
  lookups: Record<SrLookupKey, SrLookup>;
  /** Open the quick-add dialog for a lookup. */
  onNewLookup: (kind: SrLookupKey) => void;
  /** Open the Import window (Quote → Import Samples · Project → Import Pre-Log). */
  onImport: (kind: 'quote' | 'coc') => void;
  /** Shared warning (routes to the blocking modal). */
  onWarn: (message: string) => void;
  /** Field ids to flash after a "required fields missing" dialog. */
  flash: ReadonlySet<string>;
  /** True once the user has attempted a save — turns on the required tint. */
  triedSave: boolean;
  readOnly?: boolean;
  /** True once the job has been saved (JobID assigned). Locks No Of Samples and
   *  Test entirely; Matrix allows additions but blocks removal. */
  savedJob?: boolean;
  /** StatusID → display name map (from rule-data API, result set 3). */
  statusNameMap?: Record<string, string>;
}

export function RegistrationForm({
  header, patch, lookups, onNewLookup, onImport, onWarn, flash, triedSave, readOnly = false,
  savedJob = false, statusNameMap = {},
}: RegistrationFormProps) {
  const flashCls = (id: string) => (flash.has(id) ? 'sr-flash' : undefined);
  const matrixChosen = header.matrices.length > 0;
  const gate = matrixChosen ? undefined : 'Select a Sample Matrix first — the test list depends on it.';

  // NOTE: these are plain FUNCTIONS invoked inline ({label(...)}), deliberately
  // not inner components.  A component defined inside a render body gets a new
  // type on every render, so React would remount every field and the input under
  // the cursor would lose focus after each keystroke.

  /** A label whose tooltip is the manual's Entry Guidance for that field. */
  const label = (text: string, required?: boolean) => (
    <label className={required ? 'req' : undefined} title={GUIDANCE[text]}>{text}</label>
  );

  /** A lookup field: label + grid-picker combo (+ inline ＋ when addable). */
  const lookupField = (
    text: string, field: keyof SrHeader, kind: SrLookupKey,
    opts: { required?: boolean; blocked?: string; id?: string } = {},
  ) => (
    <>
      {label(text, opts.required)}
      <LookupCombo
        id={opts.id}
        lookup={lookups[kind]}
        value={String(header[field] ?? '')}
        disabled={readOnly}
        blockedReason={opts.blocked}
        onBlocked={onWarn}
        onPick={row => applyPick(field, kind, row)}
        onNew={NEWABLE[kind] && !readOnly ? () => onNewLookup(kind) : undefined}
        newTitle={NEWABLE[kind] ? `New ${NEWABLE[kind]}` : undefined}
        className={flashCls(opts.id ?? String(field))}
      />
    </>
  );

  /** A plain input field.  `emptyText` renders a literal value when the field is
   *  empty (e.g. "Autogenerate" for Job ID is a real field VALUE, not grey
   *  placeholder text). */
  const inputField = (
    text: string, field: keyof SrHeader,
    opts: {
      type?: string; required?: boolean; ro?: boolean; placeholder?: string;
      maxWidth?: string; emptyText?: string;
    } = {},
  ) => (
    <>
      {label(text, opts.required)}
      <input
        type={opts.type ?? 'text'}
        value={String(header[field] ?? '') || (opts.emptyText ?? '')}
        readOnly={opts.ro || readOnly}
        placeholder={opts.placeholder}
        style={opts.maxWidth ? { maxWidth: opts.maxWidth } : undefined}
        className={[
          opts.required && triedSave && !String(header[field] ?? '').trim() ? 'sr-req-empty' : '',
          (opts.type === 'date' || opts.type === 'time' || opts.type === 'datetime-local') && !String(header[field] ?? '') ? 'sr-dt-empty' : '',
        ].filter(Boolean).join(' ') || undefined}
        onChange={e => patch({ [field]: e.target.value } as Partial<SrHeader>)}
      />
    </>
  );

  /** Label + an arbitrary control. */
  const row = (text: string, control: ReactNode, required?: boolean) => (
    <>
      {label(text, required)}
      {control}
    </>
  );

  /** Selecting a lookup row also fills that lookup's dependent fields. */
  function applyPick(field: keyof SrHeader, kind: SrLookupKey, row: string[]) {
    const value = row[0] ?? '';
    const next: Partial<SrHeader> = { [field]: value } as Partial<SrHeader>;
    if (kind === 'client' && field === 'client') {
      next.clientAddress = row[1] ?? '';
      next.clientPhone = row[2] ?? '';
      // row[3] is the hidden client Oid (mapLookupRows appends it). Capture the
      // EXACT client so Project / Collector / Contact / Quote scope correctly
      // even when two customers share a name. N/A pick (empty row) clears it.
      next.clientOid = row[3] ?? '';
      // CLIENT IS THE MASTER FIELD — everything scoped to it is cleared, because
      // those lists are re-fetched for the new client and any held value now
      // belongs to a DIFFERENT customer (legacy "ClientName" handler clears
      // ContactName, ProjectID and Collector; the read-only project fields go
      // with ProjectID).  DELIBERATE ADDITION: legacy leaves QuoteID pointing at
      // the old client's quote — a cross-client quote would drive the wrong
      // invoice, so it is cleared here too (§18.7 — don't carry a legacy defect).
      next.clientContact = '';
      next.invoiceContact = '';
      next.projectId = '';
      next.projectName = '';
      next.projectLocation = '';
      next.collector = [];
      next.quoteId = '';
    }
    if (kind === 'project' && field === 'projectId') {
      next.projectName = row[1] ?? '';
      next.projectLocation = row[2] ?? '';
    }

    // ── XAF ObjectSpace_ObjectChanged: PaymentStatus → JobInactivation cascade ──
    // Legacy: COD auto-sets JobInactivation to "Hold"; anything else clears it.
    if (field === 'paymentStatus') {
      const action = value.toUpperCase() === 'COD' ? 'Hold' : '';
      next.jobInactivation = action;
      // Cascade into the inactivation state machine (same as picking it directly)
      applyInactivation(next, action);
    }

    // ── XAF ObjectSpace_ObjectChanged: JobInactivation → Status state machine ───
    if (field === 'jobInactivation') {
      applyInactivation(next, value);
    }

    patch(next);
  }

  /** Apply the inactivation state machine (Hold/Cancel/clear → status + cancelJob).
   *  Mutates the `next` partial in-place so the caller can batch-patch. */
  /** Resolve a status ID (e.g. "SR-2-8") to its display name via the rule-data
   *  map; falls back to the raw ID if the map hasn't loaded yet. */
  const statusName = (id: string) => statusNameMap[id] || id;

  function applyInactivation(next: Partial<SrHeader>, action: string) {
    if (action === 'Hold') {
      const isCod = (next.paymentStatus ?? header.paymentStatus)?.toUpperCase() === 'COD';
      const sid = isCod ? 'SR-2-8' : 'SR-2-7';
      next.status = statusName(sid);
      next.prevStatusId = header.status;
      next.cancelJob = false;
    } else if (action === 'Cancel') {
      if (header.receivedDate) {
        onWarn('Cannot cancel a job that has already been received.');
        next.jobInactivation = header.jobInactivation;
        return;
      }
      next.status = statusName('SR-2-6');
      next.prevStatusId = header.status;
      next.cancelJob = true;
    } else {
      next.status = header.prevStatusId || header.status;
      next.cancelJob = false;
    }
  }

  const stepNo = (delta: number) => {
    if (!matrixChosen) { onWarn('Select a Sample Matrix first.'); return; }
    const n = Math.max(1, (Number.parseInt(header.noOfSamples, 10) || 0) + delta);
    patch({ noOfSamples: String(n) });
  };

  // ── Registration-type field gating (ALPACA parity) ────────────────────────
  // Ported from the legacy BO `Samplecheckin.cs` [Appearance] rules
  // "DropOffSamplesView" / "SamplingSamplesView".  Verified authoritative:
  // Model.xafml declares NO AppearanceRules, so nothing overrides them.
  //
  //   Regular       (IsSampling = false) → hide DateExpect, ProjectManager
  //   Pre-scheduled (IsSampling = true)  → hide RelinquishedBy, DateCollected,
  //                                        Collector, ManifestNumber,
  //                                        ReceivedDate, ReceivedBy, TimeCollected
  //
  // Legacy is `RegistrationType => IsSampling ? "Pre-scheduled" : "Regular"` —
  // a COMPUTED, read-only string, which is why the field stays `ro` above.
  // The API returns "Prescheduled" while the New popup sets "Pre-scheduled", so
  // the test tolerates BOTH spellings — a strict === would silently gate nothing.
  //
  // DEVIATION (owner decision 2026-07-27): legacy's SamplingSamplesView listed
  // "…RecievedDate;RecievedBy,TimeCollected" — a COMMA where XAF splits on ';',
  // so those last two were never actually hidden.  Treated as a typo and fixed
  // here per the rule's evident intent (§18.7: don't replicate legacy defects).
  //
  // TODO(AI-29131): COCSource is Pre-scheduled-only in legacy but has no field
  // in AI yet; add it to PRESCHEDULED_ONLY when it lands.  CancelJob /
  // CancelStatus (Pre-scheduled-only, cancel workflow) are likewise pending.
  const isPrescheduled = /^pre-?scheduled$/i.test((header.registrationType ?? '').trim());
  const PRESCHEDULED_ONLY = ['dateExpect', 'projectManager', 'cocId'];
  const REGULAR_ONLY = [
    'relinquishedBy', 'dateCollected', 'collector',
    'manifestNumber', 'receivedDate', 'receivedBy', 'timeCollected',
  ];
  /** Legacy [Appearance] visibility for one field, by the current reg type. */
  const showField = (f: string): boolean =>
    PRESCHEDULED_ONLY.includes(f) ? isPrescheduled
      : REGULAR_ONLY.includes(f) ? !isPrescheduled
        : true;

  return (
    <>
      {/* ── General Information ───────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">📋</span> General Information</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {/* Prototype shows the literal word "Autogenerate" IN the read-only
                field until the id is minted — not a grey placeholder. */}
            {inputField('Job ID', 'jobId', { ro: true, emptyText: 'Autogenerate' })}
            {showField('relinquishedBy') && row('Relinquished By', (
              <LookupCombo
                lookup={lookups.relinq}
                multi
                values={header.relinquishedBy}
                disabled={readOnly}
                onToggle={next => patch({ relinquishedBy: next })}
                onNew={readOnly ? undefined : () => onNewLookup('relinq')}
                newTitle="New Relinquished By"
              />
            ))}

            {showField('receivedDate') && inputField('Received Date', 'receivedDate', { type: 'datetime-local' })}
            {showField('collector') && row('Collector', (
              <LookupCombo
                lookup={lookups.collector}
                multi
                values={header.collector}
                disabled={readOnly}
                onToggle={next => patch({ collector: next })}
                onNew={readOnly ? undefined : () => onNewLookup('collector')}
                newTitle="New Collector"
              />
            ))}

            {/* Received By reads the GLOBAL employee lookup (AI_Employee_Lookup_SP)
                supplied through `lookups.employee` — not a form-local list. */}
            {showField('receivedBy') && lookupField('Received By', 'receivedBy', 'employee', { id: 'sr-f-employee' })}
            {showField('dateCollected') && row('Date Collected', (
              <input
                type="date"
                value={header.dateCollected}
                className={!header.dateCollected ? 'sr-dt-empty' : undefined}
                readOnly={readOnly}
                onChange={e => {
                  const v = e.target.value;
                  if (v && header.receivedDate && v > header.receivedDate) {
                    onWarn('Date Collected cannot be after Received Date.');
                    return;
                  }
                  patch({ dateCollected: v });
                }}
              />
            ))}

            {inputField('Created By', 'createdBy', { ro: true })}
            {showField('timeCollected') && row('Time Collected', (
              <input
                type="time"
                value={header.timeCollected}
                className={!header.timeCollected ? 'sr-dt-empty' : undefined}
                readOnly={readOnly}
                onChange={e => {
                  const v = e.target.value;
                  if (v && !v.includes(':')) {
                    onWarn('Time Collected must be in HH:mm format.');
                    return;
                  }
                  if (v && header.dateCollected && header.receivedDate
                    && header.dateCollected === header.receivedDate.slice(0, 10)) {
                    const recTime = header.receivedDate.length > 10
                      ? header.receivedDate.slice(11, 16) : '23:59';
                    if (v > recTime) {
                      onWarn('Time Collected cannot be after Received Time on the same day.');
                      return;
                    }
                  }
                  patch({ timeCollected: v });
                }}
              />
            ))}

            {lookupField('Client', 'client', 'client', { required: true, id: 'sr-f-client' })}
            {row('Project ID', (
              <div className="sr-fldrow">
                <LookupCombo
                  id="sr-f-project"
                  lookup={lookups.project}
                  value={header.projectId}
                  disabled={readOnly}
                  onPick={r => applyPick('projectId', 'project', r)}
                  onNew={readOnly ? undefined : () => onNewLookup('project')}
                  newTitle="New Project"
                />
                <button className="sr-fldbtn" onClick={() => onImport('coc')} title="Import Pre-Log (COC)">
                  ⬇ Import
                </button>
              </div>
            ))}

            {inputField('Client Address', 'clientAddress', { ro: true })}
            {inputField('Project Name', 'projectName', { ro: true })}

            {inputField('Client Phone', 'clientPhone', { ro: true })}
            {inputField('Project Location', 'projectLocation', { ro: true })}

            {lookupField('Client Contact', 'clientContact', 'contact')}
            {lookupField('Permit', 'permit', 'permit')}

            {row('Quote ID', (
              <div className="sr-fldrow">
                <LookupCombo
                  lookup={lookups.quote}
                  value={header.quoteId}
                  disabled={readOnly}
                  onPick={r => applyPick('quoteId', 'quote', r)}
                />
                <button className="sr-fldbtn" onClick={() => onImport('quote')} title="Import Samples">
                  ⬇ Import
                </button>
              </div>
            ))}
            {lookupField('Project Category', 'projectCategory', 'category')}

            {inputField('PO #', 'poNumber')}
            {lookupField('Invoice Contact', 'invoiceContact', 'contact')}

            {lookupField('Payment Status', 'paymentStatus', 'payment')}
            {inputField('Registration Type', 'registrationType', { ro: true })}

            {showField('manifestNumber') && inputField('Manifest Number', 'manifestNumber', { type: 'number' })}
            {lookupField('Job Inactivation', 'jobInactivation', 'inact')}

            {showField('cocId') && lookupField('COC ID', 'cocId', 'cocid')}
            {!showField('cocId') && <label style={{ gridColumn: 3 }}>Status</label>}
            {!showField('cocId') && <input value={header.status} readOnly style={{ gridColumn: 4 }} />}
            {showField('cocId') && inputField('Status', 'status', { ro: true })}

            {showField('dateExpect') && inputField('Date Expect', 'dateExpect', { type: 'date' })}
            {showField('projectManager') && lookupField('Project Manager', 'projectManager', 'pm')}
          </div>
        </div>
      </section>

      {/* ── Sample Information ────────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">🧪</span> Sample Information</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {row('Sample Matrix', (
              <LookupCombo
                id="sr-f-matrix"
                lookup={lookups.matrix}
                multi
                values={header.matrices}
                disabled={readOnly}
                placeholder="Select…"
                onToggle={next => {
                  if (savedJob && header.matrices.some(v => !next.includes(v))) {
                    onWarn('Sample Matrix cannot be removed after a Job ID has been created. You may add additional matrices.');
                    return;
                  }
                  patch({ matrices: next });
                }}
                className={flashCls('sr-f-matrix')}
              />
            ), true)}
            {row('Sample Category', (
              <LookupCombo
                lookup={lookups.sampleCategory}
                multi
                values={header.sampleCategory}
                disabled={readOnly}
                onToggle={next => patch({ sampleCategory: next })}
              />
            ))}

            {row('Test', (
              /* Header-level test template — each generated sample starts from
                 this set and is customised per sample in Test Assignment. */
              <div className={matrixChosen ? undefined : 'sr-gated'}>
                <LookupCombo
                  id="sr-f-test"
                  lookup={lookups.test}
                  multi
                  values={header.testTemplate}
                  disabled={readOnly}
                  readOnlyItems={savedJob}
                  placeholder="Select…"
                  blockedReason={gate}
                  onBlocked={onWarn}
                  // XAF parity: the stored value + closed-field label is the
                  // composite "TestName|MethodNumber" (row[0]|row[1]) so two
                  // same-named tests are distinct and each resolves to its own
                  // TestMethod Oid. The grid still shows Test / Method columns.
                  rowValue={(r) => (r[1] ? `${r[0]}|${r[1]}` : (r[0] ?? ''))}
                  onToggle={(next, picked) => {
                    const composite = picked[0]
                      ? (picked[1] ? `${picked[0]}|${picked[1]}` : picked[0])
                      : '';
                    patch({
                      testTemplate: next,
                      testTemplateMethods: composite
                        ? { ...header.testTemplateMethods, [composite]: picked[1] ?? '' }
                        : header.testTemplateMethods,
                    });
                  }}
                />
              </div>
            ))}
            {inputField('Cooler ID', 'coolerId')}

            {row('No Of Samples', (
              <span className="sr-numspin">
                <button type="button" onClick={() => stepNo(-1)} disabled={savedJob} aria-label="Fewer samples">−</button>
                <input value={header.noOfSamples} type="number" min={1}
                  placeholder="—" aria-label="No Of Samples"
                  disabled={savedJob}
                  onChange={savedJob ? undefined : e => patch({ noOfSamples: e.target.value })}
                  onBlur={savedJob ? undefined : () => {
                    const n = Number.parseInt(header.noOfSamples, 10);
                    if (!header.noOfSamples || isNaN(n) || n < 1)
                      patch({ noOfSamples: '1' });
                  }}
                />
                <button type="button" onClick={() => stepNo(1)} disabled={savedJob} aria-label="More samples">＋</button>
              </span>
            ))}
            {row('Remark', (
              <textarea rows={2} value={header.remark}
                readOnly={readOnly}
                onChange={e => patch({ remark: e.target.value })} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Report Requirements ───────────────────────────────────────────── */}
      <section className="sr-sec">
        <h3 className="sr-sec-h"><span className="sr-sec-ico">📑</span> Report Requirements</h3>
        <div className="sr-sec-b">
          <div className="sr-fgrid">
            {row('TAT', (
              <LookupCombo
                lookup={lookups.tat}
                value={header.tat}
                disabled={readOnly}
                onPick={r => patch({ tat: r[0] ?? '' })}
              />
            ))}
            {lookupField('EDD Export Format', 'eddFormat', 'edd')}

            {row('Due Date', (
              <input type="datetime-local"
                value={header.dueDate ? header.dueDate.slice(0, 16) : ''}
                className={!header.dueDate ? 'sr-dt-empty' : undefined}
                onChange={e => patch({ dueDate: e.target.value })} />
            ))}
            {inputField('Report Delivery Address', 'reportDeliveryAddress')}

            {lookupField('Report Template', 'reportTemplate', 'rpt')}
            {inputField('Report Delivery Method', 'reportDeliveryMethod')}

            {lookupField('EDD Template', 'eddTemplate', 'eddtpl')}
            <span />
          </div>
        </div>
      </section>
    </>
  );
}
